/**
 * The note a speaker writes to itself before answering.
 *
 * Free's design: "agent A speaks, agent b receives it, is told to think about
 * it, that output block is fed in with the opponents output block and then the
 * agent responds. More like how a normal debate would occur."
 *
 * WHY IT SHOULD WORK, against the failure actually measured. The second speaker
 * was paraphrasing the first, and the aperture showed why: the Gardener
 * retrieves at 0.60 on a sowing question while the Hermeticist retrieves at
 * 0.46, barely over the floor, and weak facts lose to a fluent paragraph
 * already sitting in the context. The speaker had no words of its own in play.
 *
 * A deliberation pass puts some there. Before answering, the speaker writes a
 * short private note in its own voice, from its own persona. That note then
 * competes with the opponent's paragraph for the model's attention instead of
 * leaving the opponent unopposed.
 *
 * And the note is a much better SEARCH QUERY than anything else available. The
 * topic is generic, the opponent's text retrieves whatever most resembles the
 * opponent, but the note is this speaker's own articulation of what it needs to
 * look up. That is the query we actually wanted.
 *
 * NOT the model's native reasoning mode. Standing directive D1 keeps
 * `enable_thinking` off, and this does not turn it on. It is an ordinary
 * generation we issue, cap, and keep, whose text we can read and test. The
 * distinction matters: one is a hidden mode we cannot inspect, the other is a
 * turn in the transcript.
 */

/** Words that mean the speaker is stalling rather than thinking. */
const FILLER = /^(ok(ay)?|well|hmm+|let me think|thinking|alright)[,.!:\s]*/i;

/**
 * Ask the speaker to think, in its own voice, about what it needs.
 *
 * Phrased as a note to self rather than as an instruction to reason, because a
 * small model told to "reason step by step" produces scaffolding ("First, I
 * will consider...") rather than content, and scaffolding makes a worse search
 * query than plain sentences do.
 */
export function deliberatePrompt(otherPersona: string, topic: string, theirPoint: string): string {
  return [
    `${otherPersona} just argued, about ${topic}:`,
    ``,
    `"${theirPoint}"`,
    ``,
    `Before you reply, note to yourself what YOUR tradition actually says about`,
    `this, and what you would need to look up to answer them well. Plain`,
    `sentences, no preamble. Do not reply to them yet.`,
  ].join("\n");
}

/**
 * The cap is a RUNAWAY GUARD, not a budget. It was 600 and that was wrong.
 *
 * There are two truncation policies in this protocol and the blunt one used to
 * run first. `contextBudget.planCompression` is the designed one: it folds the
 * OLDEST notes into a rolling gist, only when the window is actually under
 * pressure, and never touches what was spoken. This cap fired unconditionally,
 * before the budget was ever consulted, and cut the NEWEST note.
 *
 * Measured on Free's gardening-vs-hermes run, 2026-07-27, where the opening
 * pass was 715 tokens against an 8K window, so there was no pressure at all:
 *
 *   #3 staging G   raw 1192 -> kept 549   46.1% survives
 *   #4 staging H   raw 1170 -> kept 442   37.8%
 *   #5 think G     raw  773 -> kept 463   59.9%
 *   #7 think H     raw 1276 -> kept 458   35.9%
 *
 * And the half it discarded was the same half every time. The note opens with
 * generic register-setting prose and earns its keep later, so cutting from the
 * end drops exactly the specifics: nitrogen-fixing clover, mycorrhizal
 * networks, root exudate pathways, the phragmoplast, fungal hyphae, quorum
 * sensing. All four notes reached the answer pass as throat-clearing.
 *
 * NOT fixed by cutting from the FRONT instead, which was the other idea. The
 * first sentence is the topic sentence the answer pass continues from, and the
 * staged protocol prefills the note into the assistant turn precisely so the
 * answer flows out of it. Removing the opening would break the hand-off.
 *
 * 1600 because the largest note this protocol asks for is 200 tokens, and this
 * model runs about 7.5 characters per token on technical prose, so a note that
 * respects its own stop cannot exceed ~1300. The cap now only catches a
 * generation that ignored the limit, and the budget does the budgeting.
 */
const RUNAWAY_CHARS = 1600;

/**
 * Clean a raw deliberation into something safe to keep.
 *
 * Tool syntax is stripped rather than executed: the note is written with a
 * cheap one-shot generation that has no tool loop around it, so a search line
 * left in the text would be a promise nothing keeps. The retrieval it asks for
 * happens straight afterwards, driven by this very text as the query.
 */
export function cleanDeliberation(raw: string, maxChars = RUNAWAY_CHARS): string {
  const kept = raw
    .split("\n")
    .filter((line) => {
      const t = line.trim();
      if (!t) return false;
      // Tool dialects, both of them, in any position on the line.
      if (/^(search|imagine)\s*:/i.test(t)) return false;
      if (/<\/?(search|imagine)>/i.test(t)) return false;
      return true;
    })
    .join(" ")
    .replace(/<\/?think>/gi, " ")
    .replace(/\s+/g, " ")
    .replace(FILLER, "")
    .trim();

  if (kept.length <= maxChars) return kept;
  // Cut at a sentence end rather than mid-word, so the note reads as finished.
  const cut = kept.slice(0, maxChars);
  const stop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
  return (stop > maxChars * 0.5 ? cut.slice(0, stop + 1) : cut).trim();
}

/**
 * The query to search the speaker's own lattice with.
 *
 * The note plus the topic, because the note alone can drift into voice and
 * stance while the topic keeps it anchored to the subject. Falls back to the
 * topic when deliberation produced nothing usable, which is the honest
 * behaviour: a speaker with no thought to offer should not get a worse search
 * than it had before.
 */
export function deliberationQuery(note: string, topic: string): string {
  const n = note.trim();
  if (n.length < 12) return topic;
  return `${topic}. ${n}`;
}
